import React, { useState, useEffect } from 'react'
import { Box } from '@mui/material'
import { ClockIcon } from '@heroicons/react/24/outline'
import { INTERAX_COLORS, TYPOGRAPHY } from '../../../constants/interaxColors'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'

// Extend dayjs with duration plugin
dayjs.extend(duration)

export interface CountdownTimerProps {
  targetTime: Date
  label: string
  onTimeReached?: () => void
  className?: string
}

/**
 * CountdownTimer - Displays a countdown to a target time
 * Uses UTC time for consistency across timezones
 */
export const CountdownTimer: React.FC<CountdownTimerProps> = ({ targetTime, label, onTimeReached, className = '' }) => {
  const [timeRemaining, setTimeRemaining] = useState<{
    hours: number
    minutes: number
    seconds: number
  }>({ hours: 0, minutes: 0, seconds: 0 })

  useEffect(() => {
    const target = dayjs(targetTime)

    const calculateTimeRemaining = () => {
      const now = dayjs()
      const diff = target.diff(now)

      if (diff <= 0) {
        setTimeRemaining({ hours: 0, minutes: 0, seconds: 0 })
        onTimeReached?.()
        return false
      }

      const dur = dayjs.duration(diff)
      const hours = Math.floor(dur.asHours())
      const minutes = dur.minutes()
      const seconds = dur.seconds()

      setTimeRemaining({ hours, minutes, seconds })
      return true
    }

    // Initial calculation
    if (!calculateTimeRemaining()) {
      return
    }

    // Update every second
    const interval = setInterval(() => {
      if (!calculateTimeRemaining()) {
        clearInterval(interval)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [targetTime, onTimeReached])

  // Format time parts for display
  const formatTimePart = (value: number, unit: string) => {
    if (value === 0) return null
    return `${value}${unit}`
  }

  const timeParts = [
    formatTimePart(timeRemaining.hours, 'h'),
    formatTimePart(timeRemaining.minutes, 'm'),
    formatTimePart(timeRemaining.seconds, 's')
  ].filter(Boolean)

  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: 3,
        backgroundColor: INTERAX_COLORS.heritageBlue40,
        borderRadius: '12px',
        border: `1px solid ${INTERAX_COLORS.thunderGrey20}`,
        gap: 2
      }}
    >
      {/* Clock Icon */}
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          backgroundColor: INTERAX_COLORS.sun20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <ClockIcon
          style={{
            width: 24,
            height: 24,
            color: INTERAX_COLORS.sun
          }}
        />
      </Box>

      {/* Countdown Display */}
      <Box
        sx={{
          display: 'flex',

          alignItems: 'baseline',
          gap: 1
        }}
      >
        <Box
          component="span"
          sx={{
            ...TYPOGRAPHY.headline3,
            color: INTERAX_COLORS.sun,
            fontVariantNumeric: 'tabular-nums'
          }}
        >
          {timeParts.join(' ')}
        </Box>
        <Box
          component="span"
          sx={{
            ...TYPOGRAPHY.body2,
            color: INTERAX_COLORS.thunderGrey
          }}
        >
          {label}
        </Box>
      </Box>
    </Box>
  )
}
